<template>
  <div class="flex flex-col gap-6 xl:flex-row xl:justify-center xl:gap-12 xl:items-start">
    <div class="max-w-[1000px] flex flex-col gap-6">
      <div
        class="font-heading grid justify-items-center sm:justify-items-start grid-cols-1 gap-4 xs:grid-cols-3 sm:grid-cols-[auto_repeat(4,1fr)] sm:grid-rows-2"
      >
        <div
          id="percentage-sold-container"
          class="flex items-start justify-start xs:col-start-1 xs:col-end-4 sm:col-start-1 sm:col-end-2 sm:row-start-1 sm:row-end-3 sm:mr-10"
        >
          <span class="text-9xl lg:text-[230px] font-semibold" id="percentage-sold">
            {{ soldOutPercentage }}
          </span>
          <div class="flex flex-col items-end mt-3">
            <span class="text-3xl">%</span>
            <span class="uppercase text-sm">sold</span>
          </div>
        </div>

        <div id="reservations-container" class="flex flex-col text-center sm:text-left">
          <span class="text-4xl">{{ dashboardInformation.reservations.length }}</span>
          <span class="text-sm font-light text-fg-secondary">Reservations</span>
        </div>

        <div id="guests-container" class="flex flex-col text-center sm:text-left">
          <span class="text-4xl">{{ dashboardInformation.numberOfGuests }}</span>
          <span class="text-sm font-light text-fg-secondary">Guests</span>
        </div>

        <div id="tickets-available-container" class="flex flex-col text-center sm:text-left">
          <span class="text-4xl">{{ ticketsAvailable }}</span>
          <span class="text-sm font-light text-fg-secondary">Tickets Available</span>
        </div>

        <div id="walk-ins-container" class="flex flex-col text-center sm:text-left">
          <span class="text-4xl">{{ dashboardInformation.numberOfWalkIns }}</span>
          <span class="text-sm font-light text-fg-secondary">Walk-ins</span>
        </div>

        <div id="days-to-sellout-container" class="flex flex-col text-center sm:text-left">
          <span class="text-4xl">{{ daysToSellOut }}</span>
          <span class="text-sm font-light text-fg-secondary">Days to Sellout</span>
        </div>

        <div id="days-on-sale-container" class="flex flex-col text-center sm:text-left">
          <span class="text-4xl">{{ daysOnSale }}</span>
          <span class="text-sm font-light text-fg-secondary">Days On Sale</span>
        </div>

        <div id="tickets-per-day-container" class="flex flex-col text-center sm:text-left">
          <span class="text-4xl">{{ averageTicketsPerDay }}</span>
          <span class="text-sm font-light text-fg-secondary">Tickets Per Day</span>
        </div>
      </div>

      <!--GRAPHIC-->
      <div id="graphic" class="mt-2">
        <div class="text-center">
          <span class="text-sm font-label text-fg-secondary">Max Capacity</span>
        </div>
        <div class="flex items-start justify-start mb-2">
          <svg
            :viewBox="graphicViewBox"
            class="w-full -scale-y-100 border-b border-r border-linear-subtle"
          >
            <rect
              v-for="({ cumulative, date }, idx) in ticketsPerDayWithCumulative"
              :key="date"
              :x="idx * 6"
              width="4"
              :y="0"
              :height="
                (cumulative * graphSvgHeight) /
                (selectedPerformance!.sold
                  ? _.last(ticketsPerDayWithCumulative)!.cumulative
                  : totalOfTickets)
              "
              :class="
                moment(date).isSameOrBefore(moment()) ? 'fill-linear-strong' : 'fill-transparent'
              "
            />
            <rect
              v-for="({ tickets, date }, idx) in ticketsPerDayWithCumulative"
              :key="date"
              :x="idx * 6"
              width="4"
              :y="0"
              :height="
                (tickets * graphSvgHeight) /
                (selectedPerformance!.sold
                  ? _.last(ticketsPerDayWithCumulative)!.cumulative
                  : totalOfTickets)
              "
              class="fill-fg-primary"
            />
          </svg>
          <div style="writing-mode: vertical-rl; text-orientation: mixed">
            <span class="text-sm font-label text-fg-secondary">Performance Date</span>
          </div>
        </div>
        <span class="text-sm font-label text-fg-secondary">Tickets Sold</span>
      </div>
    </div>

    <SlotPanel
      v-if="isOwner"
      id="owner-restricted-status"
      classes="w-full max-w-[384px] px-6 flex flex-col gap-6"
    >
      <h2 class="text-fg-error">Owner-Restricted Stats</h2>
      <div id="net-revenue-container" class="flex flex-col gap-4">
        <span class="font-heading text-4xl xl:text-6xl">{{ netRevenue }}</span>
        <div class="font-label text-fg-secondary flex items-center justify-start gap-1">
          <span>Net Revenue</span>
          <div class="cursor-pointer group relative">
            <InfoCircleIcon class="w-5 h-5" />
            <p
              class="hidden group-hover:block absolute top-full left-0 rounded bg-surface-secondary px-2 py-1 w-52 text-center shadow-2xl border border-linear-strong"
            >
              Tickets - Discounts - Refunds
            </p>
          </div>
        </div>
      </div>
      <div class="flex items-start justify-between gap-2 flex-wrap">
        <div id="discounts-container" class="flex flex-col">
          <span class="font-heading text-2xl">{{ totalOfDiscounts }}</span>
          <span class="font-label text-fg-secondary">Discounts</span>
        </div>
        <div id="refunds-container" class="flex flex-col">
          <span class="font-heading text-2xl">{{ totalOfRefunds }}</span>
          <span class="font-label text-fg-secondary">Refunds</span>
        </div>
        <div id="add-ons-container" class="flex flex-col">
          <span class="font-heading text-2xl">{{ totalOfAddonSales }}</span>
          <span class="font-label text-fg-secondary">Add-ons</span>
        </div>
      </div>
      <div v-show="false">
        <span class="font-label">CSV Download</span>
        <div class="flex justify-start items-start gap-8">
          <span class="text-fg-link-primary cursor-pointer">Reservations</span>
          <span class="text-fg-link-primary cursor-pointer">Transactions</span>
        </div>
      </div>
    </SlotPanel>
  </div>
</template>

<script lang="ts" setup>
import * as _ from 'lodash-es'
import moment from 'moment'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import InfoCircleIcon from '@/assets/infoCircle.svg?component'
import SlotPanel from '@/common/components/organisms/SlotPanel.vue'
import { isWalkIn } from '@/models'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import { useSettingStore } from '@/stores/setting'
import { useUserStore } from '@/stores/user'

import { ReservationRead } from '@generated/types'

interface TicketsPerDay {
  date: string
  tickets: number
}
interface TicketsPerDayWithCumulative extends TicketsPerDay {
  cumulative: number
}

const userStore = useUserStore()
const { isOwner } = storeToRefs(userStore)

const performanceReservationStore = usePerformanceReservationStore()
const { selectedPerformance, dashboardInformation } = storeToRefs(performanceReservationStore)

const validReservations = computed<ReservationRead[]>(function () {
  if (!selectedPerformance.value) return []
  return selectedPerformance.value.reservations.filter(
    r =>
      !isWalkIn(r) &&
      Number(r.amount) !== 0 &&
      r.tables.every(tableId => {
        return selectedPerformance.value!.tables.find(table => table.id === tableId)?.held === false
      }),
  )
})

const totalOfTickets = computed<number>(() => {
  if (!selectedPerformance.value) return 0
  return _.sum(
    _.map(
      selectedPerformance.value.tables.filter(t => !t.held),
      'maximum_guests',
    ),
  )
})
const soldOutPercentage = computed<number>(() => {
  if (!selectedPerformance.value) return 0
  const soldTables = selectedPerformance.value.tables.filter(t => !t.available)
  return Math.round((soldTables.length * 100) / selectedPerformance.value.tables.length)
})

const ticketsAvailable = computed<number>(() => {
  if (!selectedPerformance.value) return 0
  const availableTables = _.filter(selectedPerformance.value.tables, { available: true })
  return _.sum(_.map(availableTables, 'available_guests'))
})

const initialDateOfPerformance = computed<moment.Moment>(() => {
  if (!selectedPerformance.value) return moment()
  return selectedPerformance.value.publish_datetime &&
    selectedPerformance.value.publish_datetime !== settingStore.nowDateTime()
    ? settingStore.parseMoment(selectedPerformance.value.publish_datetime)
    : settingStore.parseMoment(selectedPerformance.value.created)
})

const settingStore = useSettingStore()
const ticketsPerDay = computed<TicketsPerDay[]>(function () {
  if (!selectedPerformance.value) return []
  const daysWithTickets = _.sortBy(
    _.map(
      _.groupBy(
        validReservations.value.map(reservation => ({
          ...reservation,
          date: settingStore.dateIsoStr(reservation.created),
        })),
        'date',
      ),
      (reservations, date) => ({
        date,
        tickets: _.sum(_.map(reservations, 'number_of_guests')),
        reservations,
      }),
    ),
    'date',
  )

  // daysWithTickets should go from initialDate to finalDate, so we fill in the gaps
  const finalDate = settingStore.parseMoment(selectedPerformance.value.datetime)
  const daysWithTicketsMap = _.keyBy(daysWithTickets, 'date')
  return _.map(_.range(finalDate.diff(initialDateOfPerformance.value, 'days') + 1), index => {
    const date = initialDateOfPerformance.value.clone().add(index, 'days').format('YYYY-MM-DD')
    return daysWithTicketsMap[date] || { date, tickets: 0 }
  })
})
const ticketsPerDayWithCumulative = computed<TicketsPerDayWithCumulative[]>(() => {
  if (!selectedPerformance.value) return []
  return _.map(ticketsPerDay.value, (tpd, index) => {
    const cumulative = _.sum(_.map(ticketsPerDay.value.slice(0, index + 1), 'tickets'))
    return { ...tpd, cumulative }
  })
})
const averageTicketsPerDay = computed<number>(() => {
  if (!selectedPerformance.value) return 0
  if (selectedPerformance.value.sold)
    return _.round(
      _.last(ticketsPerDayWithCumulative.value)!.cumulative / (Number(daysToSellOut.value) || 1),
    )
  return _.round(
    _.last(ticketsPerDayWithCumulative.value)!.cumulative / (Number(daysOnSale.value) || 1),
    1,
  )
})
const BAR_WIDTH = 6
const graphSvgHeight = computed<number>(() => {
  return (ticketsPerDayWithCumulative.value.length * BAR_WIDTH) / 3
})
const graphicViewBox = computed<string>(() => {
  return `0 0 ${ticketsPerDayWithCumulative.value.length * BAR_WIDTH} ${graphSvgHeight.value}`
})

const daysToSellOut = computed<string>(() => {
  if (!selectedPerformance.value || !selectedPerformance.value.sold) return 'N/A'
  const lastDayToSell = _.findLast(ticketsPerDay.value, tpd => tpd.tickets > 0) as TicketsPerDay
  return settingStore
    .parseMoment(lastDayToSell.date)
    .diff(initialDateOfPerformance.value, 'days')
    .toString()
})

const daysOnSale = computed<string>(() => {
  if (!selectedPerformance.value) return 'N/A'
  if (
    settingStore.parseMoment(selectedPerformance.value.datetime).isAfter(settingStore.nowDateTime())
  )
    return settingStore
      .parseMoment(settingStore.nowDateTime())
      .diff(initialDateOfPerformance.value, 'days')
      .toString()
  else
    return settingStore
      .parseMoment(selectedPerformance.value.datetime)
      .diff(settingStore.parseMoment(selectedPerformance.value.publish_datetime as string), 'days')
      .toString()
})

// O W N E R   O N L Y
const netRevenue = computed<string>(() => {
  if (!selectedPerformance.value) return '$0.00'
  return settingStore.currency(
    _.sum(selectedPerformance.value.reservations.map(reservation => +reservation.amount)),
  )
})

const totalOfDiscounts = computed<string>(() => {
  if (!selectedPerformance.value) return '$0.00'
  return settingStore.currency(
    _.sum(selectedPerformance.value.reservations.map(reservation => +reservation.discounted)),
  )
})

const totalOfRefunds = computed<string>(() => {
  if (!selectedPerformance.value) return '$0.00'
  return settingStore.currency(
    _.sum(selectedPerformance.value.reservations.map(reservation => +(reservation.refunded || 0))),
  )
})

const totalOfAddonSales = computed<string>(() => {
  if (!selectedPerformance.value) return '$0.00'
  return settingStore.currency(
    _.sum(selectedPerformance.value.reservations.map(reservation => +reservation.add_on_amount)),
  )
})
</script>
